import { del, get, post, put } from "@/lib/api";

const TOKEN_STORAGE_KEY = "dw.auth.session";
let memorySession = null;

function getNow() {
  return Date.now();
}

function normalizeSession(session) {
  if (!session?.token) {
    return null;
  }

  const expiresAt = Number(session.expiresAt || 0);
  if (expiresAt && expiresAt <= getNow()) {
    return null;
  }

  return {
    token: session.token,
    expiresAt: expiresAt || null,
  };
}

function readStoredSession() {
  if (typeof window === "undefined") {
    return normalizeSession(memorySession);
  }

  try {
    const raw = window.localStorage.getItem(TOKEN_STORAGE_KEY);
    if (!raw) {
      return normalizeSession(memorySession);
    }

    const parsed = JSON.parse(raw);
    const session = normalizeSession(parsed);
    if (!session) {
      window.localStorage.removeItem(TOKEN_STORAGE_KEY);
      memorySession = null;
      return null;
    }

    memorySession = session;
    return session;
  } catch {
    return normalizeSession(memorySession);
  }
}

function persistSession(session) {
  memorySession = session;

  if (typeof window === "undefined") {
    return;
  }

  if (!session) {
    window.localStorage.removeItem(TOKEN_STORAGE_KEY);
    return;
  }

  window.localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(session));
}

export function setToken(token, expiresIn = 604800) {
  const expiresAt = expiresIn ? getNow() + expiresIn * 1000 : null;
  persistSession({ token, expiresAt });
  return token;
}

export function getToken() {
  const session = readStoredSession();
  return session?.token || null;
}

export function clearToken() {
  persistSession(null);
}

export function logout() {
  clearToken();
}

export async function getAuthToken(userId) {
  const response = await post("/auth/token", { userId }, { useApiKey: true });

  const token = response?.token || response?.data?.token;
  const expiresIn = response?.expiresIn || response?.data?.expiresIn;
  if (token) {
    setToken(token, expiresIn);
  }

  return response;
}

export async function createUser(payload) {
  return post("/users", payload, { useApiKey: true });
}

export async function loginUser(payload) {
  return post("/auth/login", payload, { useApiKey: true });
}

export async function sendOtp(userId, channelOrPayload) {
  const payload = typeof channelOrPayload === "string"
    ? { channel: channelOrPayload }
    : channelOrPayload;

  return post(`/users/${userId}/send-otp`, payload, { useApiKey: true });
}

export async function verifyOtp(userId, payload) {
  const hasToken = Boolean(getToken());
  return post(`/users/${userId}/verify`, payload, {
    useAuth: hasToken,
    useApiKey: true,
  });
}

export async function updateUserProfile(userId, payload) {
  return put(`/users/${userId}`, payload, { useAuth: true, useApiKey: true });
}

export async function removeUser(userId) {
  return del(`/users/${userId}`, { useAuth: true, useApiKey: true });
}

export function getStoredSession() {
  return readStoredSession();
}

export async function getMe() {
  return get("/auth/me", { useAuth: true, useApiKey: true });
}
